import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gift, CheckCircle, Loader2, MapPin, Phone } from 'lucide-react';

/**
 * OrderBusinessCardsModal — concierge form for the "100 free business cards" offer.
 * Practitioner submits their shipping address; admin gets notified by email + PDF and
 * handles fulfillment manually via Vistaprint / Helloprint / etc.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - practitioner: { id, first_name, last_name, phone, address, city }
 *  - apiBase: backend base URL
 *  - token: practitioner auth token
 */
export const OrderBusinessCardsModal = ({ open, onClose, practitioner, apiBase, token }) => {
  const apiOrigin = (apiBase || '').replace(/\/$/, '');
  const cardFrontUrl = `${apiOrigin}/api/doctors/${practitioner?.id}/business-card-front.png`;
  const cardBackUrl  = `${apiOrigin}/api/doctors/${practitioner?.id}/business-card-back.png`;

  const [form, setForm] = useState({
    full_name: '',
    line1: '',
    line2: '',
    postal_code: '',
    city: '',
    country: 'France',
    phone: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Prefill from practitioner profile when opened
  useEffect(() => {
    if (open && practitioner) {
      setForm((f) => ({
        ...f,
        full_name: f.full_name || `Dr ${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim(),
        line1: f.line1 || practitioner.address || '',
        city: f.city || practitioner.city || '',
        phone: f.phone || practitioner.phone || '',
      }));
      setSuccess(false);
      setError('');
    }
  }, [open, practitioner]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setError('');

    // Basic client-side validation
    const required = ['full_name', 'line1', 'postal_code', 'city', 'phone'];
    for (const k of required) {
      if (!(form[k] || '').trim()) {
        setError("Merci de compléter tous les champs obligatoires.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${apiOrigin}/api/practitioner/business-cards/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          shipping: {
            full_name: form.full_name,
            line1: form.line1,
            line2: form.line2 || null,
            postal_code: form.postal_code,
            city: form.city,
            country: form.country,
            phone: form.phone,
          },
          quantity: 100,
          notes: form.notes || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Erreur ${res.status}`);
      }
      setSuccess(true);
    } catch (e) {
      setError(e.message || "Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!practitioner) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={onClose}
          data-testid="order-cards-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative p-5 border-b border-slate-100 bg-gradient-to-br from-emerald-50 via-white to-amber-50">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-600" />
                100 cartes de visite offertes
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                Une attention DoctoDays pour booster votre cabinet ✨
              </p>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                aria-label="Fermer"
                data-testid="order-cards-close"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {success ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  Commande enregistrée !
                </h3>
                <p className="text-slate-600 text-sm mb-6">
                  Notre équipe s'occupe de l'impression et de l'expédition.
                  Vous recevrez un email de confirmation très prochainement.<br />
                  <strong>Délai estimé : 5 à 10 jours ouvrés.</strong>
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
                  data-testid="order-cards-done"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-5 space-y-4">
                {/* Card preview */}
                <span style={{ display: 'block' }}>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <span style={{ flex: 1, display: 'block' }}>
                      <img
                        src={cardFrontUrl}
                        alt="Aperçu carte de visite recto"
                        className="w-full rounded-lg border border-slate-200 shadow-sm"
                        loading="lazy"
                      />
                      <p className="text-[10px] uppercase font-semibold text-slate-400 mt-1 text-center">Recto</p>
                    </span>
                    <span style={{ flex: 1, display: 'block' }}>
                      <img
                        src={cardBackUrl}
                        alt="Aperçu carte de visite verso"
                        className="w-full rounded-lg border border-slate-200 shadow-sm"
                        loading="lazy"
                      />
                      <p className="text-[10px] uppercase font-semibold text-slate-400 mt-1 text-center">Verso</p>
                    </span>
                  </span>
                </span>

                <p className="text-xs text-slate-500 text-center -mt-1">
                  Format pro 85×55 mm. Imprimées et expédiées par nos soins.
                </p>

                {/* Shipping address */}
                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Adresse de livraison
                  </label>

                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => update('full_name', e.target.value)}
                    placeholder="Destinataire (nom complet)"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm mb-2"
                    required
                    data-testid="ship-full-name"
                  />
                  <input
                    type="text"
                    value={form.line1}
                    onChange={(e) => update('line1', e.target.value)}
                    placeholder="Adresse (n° et rue) *"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm mb-2"
                    required
                    data-testid="ship-line1"
                  />
                  <input
                    type="text"
                    value={form.line2}
                    onChange={(e) => update('line2', e.target.value)}
                    placeholder="Complément (étage, bât., porte) — optionnel"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm mb-2"
                    data-testid="ship-line2"
                  />
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={form.postal_code}
                      onChange={(e) => update('postal_code', e.target.value)}
                      placeholder="Code postal *"
                      className="col-span-1 px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                      required
                      data-testid="ship-postal"
                    />
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                      placeholder="Ville *"
                      className="col-span-2 px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                      required
                      data-testid="ship-city"
                    />
                  </div>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => update('country', e.target.value)}
                    placeholder="Pays"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm mb-2"
                    data-testid="ship-country"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" /> Téléphone pour la livraison
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="06 12 34 56 78 *"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                    required
                    data-testid="ship-phone"
                  />
                </div>

                <div>
                  <label className="text-xs uppercase font-bold text-slate-500 mb-2 block">
                    Note (optionnel)
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => update('notes', e.target.value)}
                    placeholder="Une précision pour la livraison…"
                    rows={2}
                    maxLength={500}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none text-sm"
                    data-testid="ship-notes"
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold transition-colors shadow-sm"
                  data-testid="order-cards-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Envoi en cours…
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4" />
                      Confirmer ma commande gratuite
                    </>
                  )}
                </button>

                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                  Offre réservée aux soignants vérifiés DoctoDays. 1 commande par soignant.
                  Livraison gratuite en France métropolitaine sous 5-10 jours ouvrés.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OrderBusinessCardsModal;
